import React from 'react';
import { useContentAnalytics } from '../hooks/useAnalytics';
import { AnalyticsFilters } from '../types/analytics.types';
import { KPICard } from './KPICard';
import { SectionPerformanceTable } from './SectionPerformanceTable';
import { WordErrorsAnalysis } from './WordErrorsAnalysis';
import { WordComponentAnalysis } from './WordComponentAnalysis';
import { DifficultyDistribution } from './DifficultyDistribution';
import './ContentTab.css';

interface ContentTabProps {
  filters: AnalyticsFilters;
  onFiltersChange: (filters: AnalyticsFilters) => void;
}

export const ContentTab: React.FC<ContentTabProps> = ({ filters, onFiltersChange }) => {
  const { data, loading, error, refetch } = useContentAnalytics(filters);

  if (error) {
    return (
      <div className="content-tab">
        <div className="error-message">
          <h3>❌ Error Loading Content Data</h3>
          <p>{error.message}</p>
          <button onClick={() => refetch()} className="retry-button">
            🔄 Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="content-tab">
      {/* Summary KPIs */}
      <div className="content-summary">
        <h2>Content Performance Overview</h2>
        <div className="kpi-grid">
          {loading ? (
            <>
              <KPICard title="Total Sections" value="0" loading={true} />
              <KPICard title="Unique Words" value="0" loading={true} />
              <KPICard title="Components" value="0" loading={true} />
              <KPICard title="Most Engaged" value="N/A" loading={true} />
            </>
          ) : data?.summary ? (
            <>
              <KPICard
                title="Total Sections"
                value={data.summary.total_unique_sections.toLocaleString()}
                subtitle="Content sections"
                icon="📚"
              />
              <KPICard
                title="Unique Words"
                value={data.summary.total_unique_words.toLocaleString()}
                subtitle="Vocabulary items"
                icon="🔤"
              />
              <KPICard
                title="Components"
                value={data.summary.total_unique_components.toLocaleString()}
                subtitle="Learning activities"
                icon="🎮"
              />
              <KPICard
                title="Most Engaged"
                value={data.summary.most_engaged_section.section.substring(0, 15) + '...'}
                subtitle={`${data.summary.most_engaged_section.action_count.toLocaleString()} actions`}
                icon="🔥"
              />
            </>
          ) : null}
        </div>
      </div>

      {/* Section Performance Table */}
      {data?.sections && (
        <div className="content-section">
          <SectionPerformanceTable data={data.sections} loading={loading} />
        </div>
      )}

      {/* Word Errors Analysis */}
      {data?.words && (
        <div className="content-section">
          <WordErrorsAnalysis data={data.words} loading={loading} />
        </div>
      )}

      {/* Word + Component Analysis for Faulty Metadata Detection */}
      {data?.word_components && (
        <div className="content-section">
          <WordComponentAnalysis data={data.word_components} loading={loading} />
        </div>
      )}

      {/* Difficulty Distribution */}
      {data?.difficulty && (
        <div className="content-section">
          <DifficultyDistribution data={data.difficulty} loading={loading} />
        </div>
      )}

      {/* Additional Insights */}
      {data?.summary && !loading && (
        <div className="content-insights">
          <h3>💡 Key Content Insights</h3>
          <div className="insights-grid">
            <div className="insight-card">
              <span className="insight-icon">📊</span>
              <div className="insight-content">
                <h4>Content Coverage</h4>
                <p>
                  {data.summary.total_unique_sections.toLocaleString()} sections covering{' '}
                  {data.summary.total_unique_words.toLocaleString()} unique vocabulary words
                  across {data.summary.total_unique_components} different learning components
                </p>
              </div>
            </div>

            {data.summary.most_engaged_section && (
              <div className="insight-card">
                <span className="insight-icon">🔥</span>
                <div className="insight-content">
                  <h4>Top Performing Content</h4>
                  <p>
                    "{data.summary.most_engaged_section.section}" is the most engaged section
                    with {data.summary.most_engaged_section.action_count.toLocaleString()} learning actions
                  </p>
                </div>
              </div>
            )}

            {data.summary.least_engaged_section && (
              <div className="insight-card">
                <span className="insight-icon">⚠️</span>
                <div className="insight-content">
                  <h4>Needs Attention</h4>
                  <p>
                    "{data.summary.least_engaged_section.section}" has lower engagement
                    with {data.summary.least_engaged_section.action_count.toLocaleString()} actions.
                    Consider reviewing or promoting this content.
                  </p>
                </div>
              </div>
            )}

            {data.words && data.words.problematic_words.length > 0 && (
              <div className="insight-card">
                <span className="insight-icon">🎯</span>
                <div className="insight-content">
                  <h4>Learning Challenges</h4>
                  <p>
                    {data.words.problematic_words.length} words identified as problematic.
                    Review the word analysis to improve content difficulty or provide additional support.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ContentTab;
